// src/flows/masterclass/ai/StepAI_Purpose.tsx
import { Box, Typography } from '@mui/material';
import { AIStepLayout } from './AIStepLayout';
import { useNavigate } from 'react-router-dom';
import { useProgramCreation } from '../../ProgramCreationContext';
import { useState } from 'react';
import { API } from '../../../../../api';
import DosenInput from '../../../../../pages/new-designs/components/DosenInput';
import { useAppSelector } from '../../../../../redux/hooks';
import DosenAlert from '../../../../../pages/new-designs/components/DosenAlert';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import DosenInfoModal from '../../../../../pages/new-designs/components/DosenInfoModal';
import DosenConfirmModal from '../../../../../pages/new-designs/components/DosenConfirmModal';
import DosenButton from '../../../../ui/Buttons/DosenButton';
import { useProgramContext } from '../../../../../hooks/useProgramContext';
import { events, attributeKeys } from '../../../../../utils/amplitude';
import { trackWithBaseProps } from '../../../../../utils/amplitudeProductTracking';

interface StepAI_PurposeProps {
  model?: string;
  minDescriptionLength?: number;
  maxDescriptionLength?: number;
}

export function StepAI_Purpose({
  model = 'masterclass',
  minDescriptionLength = 200,
  maxDescriptionLength = 1000
}: StepAI_PurposeProps) {
  const navigate = useNavigate();
  const { state, dispatch } = useProgramCreation();
  const { coId } = useProgramContext();
  const orgIdParamNew = useAppSelector((state) => state.orgDetails.data?._id) || '';
  const parentIdParamNew = useAppSelector((state) => state.orgDetails.data?.grpId) || '';

  const [name, setName] = useState(state.ai.purpose.name);
  const [description, setDescription] = useState(state.ai.purpose.description);
  const [isCreatingInitialIds, setIsCreatingInitialIds] = useState(false);
  const [infoModalOpen, setInfoModalOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);

  const trimmedDescriptionLength = description.trim().length;
  // Guard the ceiling against the raw value, since that's what gets sent to the
  // backend (which rejects an over-length purpose and fails the build silently).
  const descriptionTooLong = description.length > maxDescriptionLength;
  const valid =
    name.trim().length > 0 && trimmedDescriptionLength > minDescriptionLength && !descriptionTooLong;
  const createProgramInitialIds = async () => {
    let newGroupId = state.groupId;

    if (!state.generationId) {
      const genRes = await API.generateProgramId();
      const data = genRes.data?.data || genRes.data;
      dispatch({ type: 'SET_GENERATION_ID', payload: data.id });
      if (data.groupId) {
        newGroupId = data.groupId;
        dispatch({ type: 'SET_GROUP_ID', payload: data.groupId });
      }
    }

    if (!newGroupId) {
      const groupRes = await API.createGroup({
        orgId: orgIdParamNew,
        name: name,
        parent: parentIdParamNew
      });

      newGroupId = groupRes.data.groupDetails.grpId;
      dispatch({ type: 'SET_GROUP_ID', payload: newGroupId ?? '' });

      // Program lands in the In Design column the moment it's created.
      trackWithBaseProps(events.programInDesign, {
        [attributeKeys.programName]: name,
        [attributeKeys.adminProgramStatus]: 'In Design',
        [attributeKeys.programCreationFlow]: 'AI MD',
        grpId: newGroupId
      });
    }

    if (newGroupId) {
      const formData = new FormData();
      formData.append('model', model);
      formData.append('includeTask', 'true');
      formData.append('allowEditing', 'false');
      formData.append('mentee', 'mentee');
      formData.append('mentor', 'mentor');

      await API.updateGroupInfo({
        orgId: coId ?? '',
        grpId: newGroupId ?? '',
        formData
      });
    }
  };

  const handleNext = async () => {
    if (isCreatingInitialIds || !valid) return;
    setIsCreatingInitialIds(true);
    try {
      await createProgramInitialIds();
    } finally {
      setIsCreatingInitialIds(false);
    }
    dispatch({ type: 'UPDATE_AI_PURPOSE', payload: { name, description } });
    dispatch({ type: 'UPDATE_MASTERCLASS', payload: { title: name, description } });
    dispatch({ type: 'SET_AI_STEP', payload: 3 });
    navigate('../objectives');
  };

  const handleCancel = () => {
    setConfirmModalOpen(true);
  };

  const handleConfirmClose = () => {
    setConfirmModalOpen(false);
  };

  const handleSaveDraft = () => {
    setConfirmModalOpen(false);
    navigate(-1);
  };

  return (
    <AIStepLayout
      stepTitle="Purpose"
      title="General purpose"
      onBack={handleCancel}
      prevLabel="Cancel"
      onNext={handleNext}
      nextDisabled={!valid}
      nextLoading={isCreatingInitialIds}
      nextLoadingLabel="Building..."
      bottomContent={
        <DosenAlert
          text="What is Elliott all about?"
          showIcon
          icon={<InfoOutlinedIcon sx={{ width: '15px', height: '15px', color: '#FDFDFD' }} />}
          showLink
          linkName="Learn more"
          onLinkClick={() => setInfoModalOpen(true)}
        />
      }
    >
      <DosenInput label="Program name" value={name} onChange={(e) => setName(e.target.value)} fullWidth />

      <DosenInput
        label="Program purpose"
        multiline
        rows={5}
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        minChars={minDescriptionLength}
        error={descriptionTooLong}
        helperText={
          descriptionTooLong
            ? `Program purpose must be ${maxDescriptionLength.toLocaleString()} characters or fewer (currently ${description.length.toLocaleString()}).`
            : undefined
        }
        fullWidth
      />
      <DosenInfoModal
        open={infoModalOpen}
        onClose={() => setInfoModalOpen(false)}
        title="Elliott helps you build in minutes."
        text="Design and publish your Course in minutes with Elliott, step by step, from a few inputs. Resulting in expert-level content creation faster and easier than ever."
      />
      <DosenConfirmModal open={confirmModalOpen} onClose={handleConfirmClose} title="Why leave now?">
        <Typography
          sx={{
            textAlign: 'center',
            color: 'var(--Text-text-secondary, #B6B6B6)',
            fontSize: 14,
            fontWeight: 500,
            fontFamily: 'Inter, sans-serif',
            lineHeight: '20px'
          }}
        >
          You’ve made good progress! If you leave now, we’ll save your work as a draft so you can pick up right where you
          left off. Want to keep editing or save and come back later?
        </Typography>
        <Box sx={{ display: 'flex', gap: 3.5, width: '100%' }}>
          <DosenButton tone="basic" sizeVariant="sm" fullWidth onClick={handleSaveDraft}>
            Save a Draft for Later
          </DosenButton>
          <DosenButton fullWidth sizeVariant="sm" onClick={handleConfirmClose}>
            Continue Building
          </DosenButton>
        </Box>
      </DosenConfirmModal>
    </AIStepLayout>
  );
}
